import type {
  ContactAction,
  ContactsManageApiResponse,
  ContactsManageRequest,
  ErrorResponse,
  PersonShort,
} from "features/contacts/data/types";
import { useDataQuery } from "hooks/useData";
import React, { useEffect, useMemo, useState } from "react";

import { apiHost } from "../../../../consts";

import type { PersonContactProps } from "./PersonContact.types";
export type { ContactStatus } from "./PersonContact.types";
import { getStatusByAction } from "./PersonContact.utils";

const PersonContact: React.FC<PersonContactProps> = ({
  personId,
  contactStatus,
  onActionDone,
}) => {
  const person = useDataQuery<PersonShort | null>(
    `people/${personId}/`,
    !personId,
    null,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState(contactStatus);

  useEffect(() => {
    setLocalStatus(contactStatus);
  }, [contactStatus]);

  const fullName = useMemo(() => {
    if (!person) return "";

    return [person.firstName, person.secondName].filter(Boolean).join(" ");
  }, [person]);

  const manageContact = async (action: ContactAction) => {
    setIsLoading(true);
    setError(null);
    try {
      const body: ContactsManageRequest = { person_id: personId, action };
      const response = await fetch(
        `${apiHost}/solutions/api/contacts/manage/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        },
      );
      const data: ContactsManageApiResponse = await response.json();

      if (!response.ok || "error" in data) {
        throw new Error((data as ErrorResponse).error || "Request failed");
      }
      const nextStatus = getStatusByAction(localStatus, action);
      setLocalStatus(nextStatus);
      onActionDone?.(nextStatus, action);
    } catch (e: any) {
      setError(e?.message || "Unknown error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!person) return <div>Загрузка пользователя...</div>;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 12,
        border: "1px solid #ddd",
        borderRadius: 8,
      }}
    >
      <img
        src={
          person.avatarUrl || "https://randomuser.me/api/portraits/lego/1.jpg"
        }
        alt={fullName || `Person ${person.id}`}
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>
          <a href={`/#/people/${person.id}`}>
            {fullName || `Person ${person.id}`}
          </a>
        </div>
        {error && <div style={{ color: "#c00", marginTop: 6 }}>{error}</div>}
      </div>
      {localStatus === "none" && (
        <button
          onClick={() => manageContact("add")}
          disabled={isLoading}
        >
          {isLoading ? "Отправка..." : "Добавить в контакты"}
        </button>
      )}
      {localStatus === "my_request" && (
        <button
          onClick={() => manageContact("remove")}
          disabled={isLoading}
        >
          {isLoading ? "Обработка..." : "Отменить запрос"}
        </button>
      )}
      {localStatus === "mutual" && (
        <button
          onClick={() => manageContact("remove")}
          disabled={isLoading}
        >
          {isLoading ? "Обработка..." : "Удалить из контактов"}
        </button>
      )}
      {localStatus === "their_request" && (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => manageContact("add")}
            disabled={isLoading}
          >
            {isLoading ? "Обработка..." : "Принять"}
          </button>
          <button
            onClick={() => manageContact("deny")}
            disabled={isLoading}
          >
            {isLoading ? "Обработка..." : "Отклонить"}
          </button>
        </div>
      )}
    </div>
  );
};

export default PersonContact;
